import { AlertTriangle, Gauge, Loader2, TrendingUp } from "lucide-react";
import {
  useAronConversationBudget,
  useRaiseAronConversationBudget,
} from "../api/aronApi";

const fmtInt = (n: number) => n.toLocaleString("pl-PL");
/** Limity ARON-a są KWOTOWE (USD) — kwota po polsku, dwa miejsca po przecinku. */
const usd = (value: string | number) => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return `$${(Number.isFinite(n) ? n : 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Rich banner shown when a turn stopped on a cost budget. For the per-conversation
 * cap it shows spend vs limit and — for a technical director / CEO — a one-click
 * "raise this thread" action. For the daily cap it's an informational note.
 */
export function AronBudgetNotice({
  conversationId,
  reason,
}: {
  conversationId: string | null;
  reason: "budget" | "daily_budget";
}) {
  if (reason === "daily_budget") {
    return (
      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
        <Gauge size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Wyczerpano dzienny limit kosztów ARON
          </p>
          <p className="mt-0.5 text-amber-800/90 dark:text-amber-300/90">
            Limit jest kwotowy i odnawia się jutro. W razie potrzeby dyrektor
            techniczny może zwiększyć Twój dzienny limit.
          </p>
        </div>
      </div>
    );
  }
  return (
    <ConversationBudgetNotice conversationId={conversationId} />
  );
}

function ConversationBudgetNotice({
  conversationId,
}: {
  conversationId: string | null;
}) {
  const { data, isLoading } = useAronConversationBudget(conversationId, true);
  const raise = useRaiseAronConversationBudget(conversationId ?? "");

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Ta rozmowa wyczerpała swój budżet
          </p>
          <p className="mt-0.5 text-amber-800/90 dark:text-amber-300/90">
            {isLoading || !data ? (
              "Sprawdzam zużycie…"
            ) : (
              <>
                Wydano <span className="font-medium">{usd(data.used_usd)}</span> z{" "}
                <span className="font-medium">{usd(data.budget_usd)}</span>
                {" "}({fmtInt(data.used_tokens)} tokenów). Zacznij nową rozmowę,
                aby kontynuować — albo zwiększ budżet tej rozmowy poniżej.
              </>
            )}
          </p>

          {data?.can_raise && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={raise.isPending}
                onClick={() => raise.mutate(data.suggested_budget_usd)}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {raise.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <TrendingUp size={13} />
                )}
                Zwiększ budżet do {usd(data.suggested_budget_usd)}
              </button>
              {raise.isSuccess && (
                <span className="text-xs text-green-700 dark:text-green-400">
                  Zwiększono — wyślij kolejną wiadomość, aby kontynuować.
                </span>
              )}
              {raise.isError && (
                <span className="text-xs text-red-600">
                  Nie udało się zwiększyć budżetu.
                </span>
              )}
            </div>
          )}
          {data && !data.can_raise && (
            <p className="mt-1.5 text-xs text-amber-700/80 dark:text-amber-300/70">
              Zwiększenie budżetu tej rozmowy może wykonać dyrektor techniczny.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
